import React, { useState } from 'react';
import './DataDeletion.css';
import Sidebar from '../../components/Sidebar';
import { supabase } from '../../lib/supabase';

const DataDeletion = () => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Initial, 2: Instructions, 3: Confirmation

  const handleDeleteAccount = async () => {
    if (confirmationText !== 'アカウントを削除します') {
      setErrors({ confirmation: '正確な確認文を入力してください。' });
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setErrors({ general: 'ユーザーが見つかりません。' });
        return;
      }

      // Sign out the user
      await supabase.auth.signOut();
      
      // Redirect to login page
      window.location.href = '/login';
      
    } catch (error) {
      console.error('Account deletion failed:', error);
      setErrors({ general: 'アカウント削除処理でエラーが発生しました。サポートまでお問い合わせください。' });
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setIsConfirmationOpen(false);
    setConfirmationText('');
    setErrors({});
    setStep(1);
  };

  return (
    <div className="system-page">
      <div><Sidebar /></div>
      <main className="data-deletion-content-wrapper">
        <div className="background-overlay"></div>
        <div className="data-deletion-content">
          <div className="data-deletion-header">
            <h1>データ削除手順</h1>
            <p className="header-description">
              アカウントとすべての関連データを完全に削除するための手順をご確認ください。
            </p>
          </div>

          {step === 1 && (
            <div className="step-content">
              <div className="warning-section">
                <div className="warning-icon">⚠️</div>
                <h2>重要な警告</h2>
                <div className="warning-list">
                  <p>アカウントを削除すると、以下のデータが<strong>完全に削除</strong>され、<strong>復元することはできません</strong>：</p>
                  <ul>
                    <li>プロフィール情報（名前、メール、写真など）</li>
                    <li>学習履歴とプログレス</li>
                    <li>コース進捗データ</li>
                    <li>保存したビデオやメモ</li>
                    <li>アカウント設定</li>
                    <li>その他すべての個人データ</li>
                  </ul>
                </div>
              </div>

              <div className="alternatives-section">
                <h3>削除前の検討事項</h3>
                <div className="alternatives-grid">
                  <div className="alternative-item">
                    <h4>一時的な停止</h4>
                    <p>アカウントを一時的に無効化したい場合は、サポートまでご連絡ください。</p>
                  </div>
                  <div className="alternative-item">
                    <h4>データエクスポート</h4>
                    <p>重要なデータがある場合は、削除前にエクスポートをご検討ください。</p>
                  </div>
                  <div className="alternative-item">
                    <h4>部分的削除</h4>
                    <p>特定のデータのみ削除したい場合は、設定ページで個別に削除できます。</p>
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <button 
                  className="btn-secondary"
                  onClick={() => window.history.back()}
                >
                  戻る
                </button>
                <button 
                  className="btn-danger"
                  onClick={() => setStep(2)}
                >
                  削除手順を確認する
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="step-content">
              <div className="instructions-section">
                <h2>削除手順</h2>
                <div className="steps-list">
                  <div className="step-item">
                    <div className="step-number">1</div>
                    <div className="step-description">
                      <h4>データのバックアップ（必要に応じて）</h4>
                      <p>重要なデータがある場合は、この段階でバックアップを取得してください。</p>
                    </div>
                  </div>
                  
                  <div className="step-item">
                    <div className="step-number">2</div>
                    <div className="step-description">
                      <h4>削除の最終確認</h4>
                      <p>削除するデータの内容を再度確認し、本当に削除してよいかご検討ください。</p>
                    </div>
                  </div>
                  
                  <div className="step-item">
                    <div className="step-number">3</div>
                    <div className="step-description">
                      <h4>確認文の入力</h4>
                      <p>誤操作を防ぐため、指定された確認文を正確に入力していただきます。</p>
                    </div>
                  </div>
                  
                  <div className="step-item">
                    <div className="step-number">4</div>
                    <div className="step-description">
                      <h4>削除処理の実行</h4>
                      <p>削除処理が開始され、すべてのデータが永続的に削除されます。</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <button 
                  className="btn-secondary"
                  onClick={() => setStep(1)}
                >
                  戻る
                </button>
                <button 
                  className="btn-danger"
                  onClick={() => setStep(3)}
                >
                  削除を実行する
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="step-content">
              <div className="confirmation-section">
                <h2>最終確認</h2>
                <p className="confirmation-text">
                  アカウントとすべてのデータを完全に削除します。この操作は取り消すことができません。
                </p>
                
                {errors.general && (
                  <div className="error-message">{errors.general}</div>
                )}

                <div className="form-group">
                  <label htmlFor="confirmation-input">
                    以下の文を正確に入力してください：<br />
                    <strong>「アカウントを削除します」</strong>
                  </label>
                  <input
                    id="confirmation-input"
                    type="text"
                    value={confirmationText}
                    onChange={(e) => {
                      setConfirmationText(e.target.value);
                      if (errors.confirmation) {
                        setErrors(prev => ({ ...prev, confirmation: '' }));
                      }
                    }}
                    placeholder="アカウントを削除します"
                    disabled={loading}
                  />
                  {errors.confirmation && (
                    <p className="error-message">{errors.confirmation}</p>
                  )}
                </div>

                <div className="final-warning">
                  <p><strong>注意：</strong>この操作により、あなたのアカウントとすべての関連データが永続的に削除されます。</p>
                </div>
              </div>

              <div className="action-buttons">
                <button 
                  className="btn-secondary"
                  onClick={resetState}
                  disabled={loading}
                >
                  キャンセル
                </button>
                <button 
                  className="btn-danger btn-delete"
                  onClick={handleDeleteAccount}
                  disabled={loading || confirmationText !== 'アカウントを削除します'}
                >
                  {loading ? '削除中...' : 'アカウントを削除する'}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DataDeletion;